import { createElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { AssistantObjectRef, AssistantUndoableDTO } from '@contracts/assistant';
import type { Id } from '@contracts/common';
import { ApiClientError } from '../../api/client';
import { notify } from '../../lib/toast';
import { claimUndo, releaseUndo } from '../../lib/undo-slot';
import { showUndoToast } from '../../lib/undo-toast';
import { ASSISTANT_KEY, undoAction } from './api';
import { cancelMission } from './missions/api';
import { CANCEL_MISSION } from './missions/labels';

const TOAST_ID = 'undo-hana';
/** Hana's name in the ⌘Z slot (D85). */
export const HANA_UNDO_OWNER = 'hana';

export type HanaUndo = {
  /** Hana's light write offers its undo: a toast, and the ⌘Z slot until the undo window ends (D144). */
  offer: (u: AssistantUndoableDTO, objects?: readonly AssistantObjectRef[]) => void;
  /** D187: one command's undo; the request goes through the group's newest member, which undoes all of it. */
  undo: (u: Pick<AssistantUndoableDTO, 'actionId' | 'groupId'>) => Promise<'undone' | 'refused'>;
  /** Groups (`groupId`) undone here. */
  undone: ReadonlySet<Id>;
  /** Groups the server says can no longer be undone (UNDO_EXPIRED, ACTION_CLOSED): their button is hidden (D144). */
  gone: ReadonlySet<Id>;
  /** S7 G3: a mission's undo refused as stale (`hint: 'cancel_mission'`), group → mission: "Hủy nhiệm vụ" instead. */
  stale: ReadonlyMap<Id, Id>;
  /** "Hủy nhiệm vụ": the mission of a stale group cancelled at its current version. */
  cancelMission: (groupId: Id) => Promise<void>;
};

/**
 * Hana's undo (spec §8.1, D85, D144). It lives in the provider, so the toast and ⌘Z outlive a closed panel. One click
 * is one request; a refusal reads as the server wrote it, and one that says the undo is over hides the button for good.
 */
export function useHanaUndo({ onUndone }: { onUndone: (objects: readonly AssistantObjectRef[]) => void }): HanaUndo {
  const queryClient = useQueryClient();
  const [undone, setUndone] = useState<ReadonlySet<Id>>(() => new Set());
  const [gone, setGone] = useState<ReadonlySet<Id>>(() => new Set());
  const [stale, setStale] = useState<ReadonlyMap<Id, Id>>(() => new Map());
  const staleRef = useRef(stale);
  staleRef.current = stale;
  const shown = useRef(0);
  const busy = useRef(new Set<Id>());
  const expiry = useRef<number | null>(null);
  // The group the toast and ⌘Z hold now: undoing an older one leaves them to the newer (P2 review F7). Everything
  // here is keyed by the command's group (D187), never by one write of it.
  const latest = useRef<Id | null>(null);
  // The objects the group's writes touched (each write's last step before `undo_available`), for the page to refresh.
  const touched = useRef(new Map<Id, readonly AssistantObjectRef[]>());
  const done = useRef(onUndone);
  useEffect(() => { done.current = onUndone; });
  useEffect(() => () => {
    if (expiry.current !== null) window.clearTimeout(expiry.current);
    notify.dismiss(TOAST_ID);
    releaseUndo(HANA_UNDO_OWNER);
  }, []);

  /** The toast and the ⌘Z claim go only with the action they offer. */
  const letGo = useCallback((groupId: Id) => {
    if (latest.current !== groupId) return;
    latest.current = null;
    if (expiry.current !== null) window.clearTimeout(expiry.current);
    expiry.current = null;
    notify.dismiss(TOAST_ID);
    releaseUndo(HANA_UNDO_OWNER);
  }, []);

  const undo = useCallback(async ({ actionId, groupId }: Pick<AssistantUndoableDTO, 'actionId' | 'groupId'>) => {
    if (busy.current.has(groupId)) return 'refused' as const;           // one click, one request
    busy.current.add(groupId);
    try {
      await undoAction(actionId, crypto.randomUUID());
      setUndone((s) => new Set(s).add(groupId));
      letGo(groupId);
      await queryClient.invalidateQueries({ queryKey: ASSISTANT_KEY });
      done.current(touched.current.get(groupId) ?? []);
      return 'undone' as const;
    } catch (e) {
      const missionId = e instanceof ApiClientError && e.code === 'UNDO_STALE' && e.extra?.hint === 'cancel_mission'
        ? e.extra.missionId : undefined;
      if (missionId !== undefined) {
        // G3: the tree was changed by someone else, so the mission cannot be taken back — it can still be stopped.
        setStale((m) => new Map(m).set(groupId, missionId));
        letGo(groupId);
        notify.error((e as ApiClientError).message, { action: createElement('button',
          { type: 'button', 'data-button': '', onClick: () => void cancelRef.current(groupId) }, CANCEL_MISSION.action) });
        return 'refused' as const;
      }
      notify.error(e instanceof ApiClientError ? e.message : 'Đã có lỗi xảy ra, vui lòng thử lại');
      if (e instanceof ApiClientError && (e.code === 'UNDO_EXPIRED' || e.code === 'ACTION_CLOSED')) {
        setGone((s) => new Set(s).add(groupId));                          // D144: the button goes for good
        letGo(groupId);
      }
      return 'refused' as const;
    } finally {
      busy.current.delete(groupId);
    }
  }, [queryClient, letGo]);

  const cancelling = useRef(new Set<Id>());
  const cancel = useCallback(async (groupId: Id) => {
    const missionId = staleRef.current.get(groupId);
    if (missionId === undefined || cancelling.current.has(groupId)) return;
    cancelling.current.add(groupId);
    try {
      await cancelMission(missionId, crypto.randomUUID());
      setStale((m) => { const next = new Map(m); next.delete(groupId); return next; });
      setGone((g) => new Set(g).add(groupId));
      notify.success(CANCEL_MISSION.done);
      await queryClient.invalidateQueries({ queryKey: ASSISTANT_KEY });
    } catch (e) {
      notify.error(e instanceof ApiClientError ? e.message : 'Đã có lỗi xảy ra, vui lòng thử lại');
    } finally {
      cancelling.current.delete(groupId);
    }
  }, [queryClient]);
  const cancelRef = useRef(cancel);
  cancelRef.current = cancel;

  const offer = useCallback((u: AssistantUndoableDTO, objects: readonly AssistantObjectRef[] = []) => {
    // A later write of the same command adds what it touched, and the offer moves to the group's newest member.
    touched.current.set(u.groupId, [...(touched.current.get(u.groupId) ?? []), ...objects]);
    latest.current = u.groupId;
    shown.current += 1;
    showUndoToast({ toastId: TOAST_ID, text: u.summary, key: shown.current, onUndo: () => void undo(u) });
    claimUndo(HANA_UNDO_OWNER, () => void undo(u), () => notify.dismiss(TOAST_ID));
    // D144: the ⌘Z claim ends with the undo window; a newer offer replaces the timer.
    if (expiry.current !== null) window.clearTimeout(expiry.current);
    expiry.current = window.setTimeout(() => letGo(u.groupId), Math.max(0, Date.parse(u.expiresAt) - Date.now()));
  }, [undo, letGo]);

  return useMemo(() => ({ offer, undo, undone, gone, stale, cancelMission: cancel }),
    [offer, undo, undone, gone, stale, cancel]);
}
